import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, Building2, Mail, Phone as PhoneIcon, ExternalLink,
  KeyRound, Save, Archive, Copy, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const PRESET_COLORS = ["#FFB000", "#0EA5E9", "#22C55E", "#A855F7", "#EF4444", "#F97316", "#06B6D4", "#EC4899"];

export default function ClientEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    api.get(`/admin/clients/${id}`).then(({ data }) => {
      setClient(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-white/55 py-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!client) return <div className="text-white/55 py-12">Client not found.</div>;

  const set = (k, v) => setClient((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name: client.name,
        accent_color: client.accent_color,
        logo_url: client.logo_url || null,
        contact_name: client.contact_name,
        contact_email: client.contact_email,
        contact_phone: client.contact_phone || null,
        industry: client.industry || null,
        notes: client.notes || null,
        status: client.status,
      };
      await api.patch(`/admin/clients/${id}`, patch);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset ${client.contact_email}'s password and email them a new temp password?`)) return;
    try {
      const { data } = await api.post(`/admin/clients/${id}/reset-password`, { notify: true });
      setResetResult(data);
      toast.success("Reset done — temp password emailed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive ${client.name}? Their portal stops working immediately.`)) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success("Archived");
      navigate("/clients");
    } catch (err) {
      toast.error("Archive failed");
    }
  };

  const portalUrl = `${window.location.origin}/portal/${client.slug}`;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="client-edit-page">
      <button
        onClick={() => navigate("/clients")}
        className="flex items-center gap-2 text-sm text-white/55 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> All clients
      </button>

      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden border border-white/10"
          style={{ background: client.logo_url ? "transparent" : `${client.accent_color}20` }}
        >
          {client.logo_url ? (
            <img src={client.logo_url} alt="" className="w-full h-full object-contain" />
          ) : (
            <Building2 className="w-6 h-6" style={{ color: client.accent_color }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{client.name}</h1>
          <div className="text-[12px] text-white/45 mt-1 flex items-center gap-2 flex-wrap">
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB000] hover:underline flex items-center gap-1">
              {portalUrl} <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Copied"); }}
              className="text-white/40 hover:text-white"
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Users", value: client.stats?.users || 0 },
          { label: "Calls", value: client.stats?.calls || 0 },
          { label: "Appointments", value: client.stats?.appointments || 0 },
          { label: "Campaigns", value: client.stats?.campaigns || 0 },
        ].map((s) => (
          <div key={s.label} className="bg-[#121214] border border-white/5 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.12em] text-white/40">{s.label}</div>
            <div className="text-2xl font-bold mt-1.5" style={{ color: client.accent_color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {resetResult && (
        <div className="bg-[#121214] border border-emerald-500/30 rounded-xl p-4 text-sm" data-testid="reset-result">
          <div className="flex items-center gap-2 text-emerald-300 font-medium mb-2"><KeyRound className="w-4 h-4" /> New password</div>
          <div className="flex items-center gap-2"><code className="font-mono text-[#FFB000]">{resetResult.temp_password}</code><button onClick={() => navigator.clipboard.writeText(resetResult.temp_password)} className="text-white/50 hover:text-white"><Copy className="w-3.5 h-3.5" /></button></div>
          <div className="text-[11px] text-white/40 mt-2">Welcome email re-sent. Client must change on next login.</div>
        </div>
      )}

      {/* Edit form */}
      <div className="bg-[#121214] border border-white/5 rounded-2xl p-7 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company name" testid="edit-name"><input className="inp" value={client.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Industry"><input className="inp" value={client.industry || ""} onChange={(e) => set("industry", e.target.value)} /></Field>
        </div>

        <Field label="Accent color">
          <div className="flex items-center gap-2.5 flex-wrap">
            {PRESET_COLORS.map((c) => (
              <button type="button" key={c} onClick={() => set("accent_color", c)} className={`w-7 h-7 rounded-lg ${client.accent_color === c ? "ring-2 ring-white/60 scale-110" : "ring-1 ring-white/10"}`} style={{ background: c }} />
            ))}
            <input className="inp !w-28 font-mono text-xs" value={client.accent_color} onChange={(e) => set("accent_color", e.target.value)} />
          </div>
        </Field>

        <Field label="Logo URL"><input className="inp" value={client.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." /></Field>

        <div className="pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB000] font-medium mb-3">Primary contact</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><input className="inp" value={client.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></Field>
            <Field label="Email"><input className="inp" type="email" value={client.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Phone"><input className="inp" type="tel" value={client.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="Status">
              <select className="inp" value={client.status} onChange={(e) => set("status", e.target.value)} data-testid="edit-status">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
          </div>
        </div>

        <Field label="Internal notes"><textarea rows={3} className="inp" value={client.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>

        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div className="flex items-center gap-2">
            <button onClick={resetPassword} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20" data-testid="reset-password-btn">
              <KeyRound className="w-3.5 h-3.5" /> Reset password
            </button>
            <button onClick={archive} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-rose-300 bg-rose-500/10 border border-rose-500/30 hover:bg-rose-500/20" data-testid="archive-client-btn">
              <Archive className="w-3.5 h-3.5" /> Archive
            </button>
          </div>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633] disabled:opacity-50" data-testid="save-client-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </div>

      {/* Users list */}
      {client.users && client.users.length > 0 && (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-5" data-testid="client-users-list">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB000] font-medium mb-3">Users</div>
          {client.users.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <div>
                <div className="text-sm">{u.name}</div>
                <div className="text-xs text-white/45">{u.email}</div>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-white/55">
                {u.must_change_password && <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> needs password change</span>}
                {u.disabled && <span className="text-rose-400">disabled</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .inp { width: 100%; padding: 10px 13px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 9px; color: #fff; font-size: 14px; outline: none; transition: border-color .2s; font-family: 'Inter', sans-serif; }
        .inp:focus { border-color: #FFB000; background: rgba(255,176,0,0.04); }
      `}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[11px] font-medium text-white/65 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
